import { useEffect, useState } from "react";
import { useQueryClient, useQuery, useMutation } from "@tanstack/react-query";
import { apiRequest, parseErrorMessage } from "@/lib/queryClient";
import { Card, CardHeader, CardContent, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { useToast } from "@/hooks/use-toast";
import { UserPlus2, UserMinus, EyeOff, Megaphone } from "lucide-react";
import AppLayout from "@/components/app-layout";
import { AdminAccessGuard } from "@/components/admin-access-guard";
import { usePageTitle } from "@/hooks/use-page-title";
import { GuideButton } from "@/components/guide-button";
import { GuideNudgeBanner } from "@/components/guide-nudge-banner";
import { isBetaCatalogKey, isAdminGateKey } from "@shared/beta-catalog";
import { useBetaToolAccess } from "@/hooks/use-beta-tool-access";
import { BetaToolManagers } from "@/components/beta-tool-managers";

// Task #3976 — admin example used as the message placeholder.
const GATE_MESSAGE_PLACEHOLDER = "Under final testing. Expected release date: July 1, 2026.";

type GateTarget = {
  key: string;
  name: string;
  description: string | null;
  group: string | null;
  navHrefs: string[];
  routePrefixes: string[];
  routeExactPaths?: string[];
  apiPrefixes: string[];
  gateEnabled: boolean;
  gateMode: "hidden" | "coming_soon";
  gateMessage: string | null;
  userCount: number;
};

type BetaFeatureRow = {
  id: number;
  key: string;
  name: string;
  description: string | null;
  isActive: boolean;
  allowSelfOptIn: boolean;
  createdAt: string;
  userCount: number;
};

type BetaUser = {
  id: number;
  email: string;
  name: string | null;
  grantedAt: string;
  grantedBy: number | null;
};

type SearchUser = { id: number; email: string; name: string | null };

export default function AppAdminBetaFeatures() {
  const access = useBetaToolAccess();
  return <AppLayout>
    {!access.allowed ? (
      <div className="p-6" role="status">
        {access.loading ? "Checking Beta Access permission…" : "Beta Access is unavailable. You may not have permission, or access could not be verified."}
        {!access.loading && <Button variant="outline" onClick={() => void access.refetch()}>Check again</Button>}
      </div>
    ) : <BetaFeaturesContent canAssign={access.canAssign} />}
  </AppLayout>;
}

function BetaFeaturesContent({ canAssign }: { canAssign: boolean }) {
  const queryClient = useQueryClient();
  usePageTitle("Beta Features");
  const { toast } = useToast();

  const featuresQuery = useQuery<BetaFeatureRow[]>({
    queryKey: ["/api/app/admin/beta-features"],
  });
  const features = (featuresQuery.data ?? []).filter(feature => isBetaCatalogKey(feature.key));

  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const selectedFeature = features.find((f) => f.key === selectedKey) ?? null;

  const usersQuery = useQuery<BetaUser[]>({
    queryKey: ["/api/app/admin/beta-features", selectedKey, "users"],
    queryFn: async () => {
      if (!selectedKey) return [];
      const res = await fetch(`/api/app/admin/beta-features/${selectedKey}/users`);
      if (!res.ok) throw new Error("Failed to load");
      return res.json();
    },
    enabled: !!selectedKey,
  });

  const [searchQuery, setSearchQuery] = useState("");
  const searchResultsQuery = useQuery<SearchUser[]>({
    queryKey: ["/api/app/admin/beta-search-users", searchQuery],
    queryFn: async () => {
      if (!searchQuery || searchQuery.length < 2) return [];
      const res = await fetch(
        `/api/app/admin/beta-search-users?q=${encodeURIComponent(searchQuery)}`,
      );
      if (!res.ok) return [];
      return res.json();
    },
    enabled: searchQuery.length >= 2,
  });

  const grantMutation = useMutation({
    mutationFn: async ({ featureKey, userId }: { featureKey: string; userId: number }) => {
      await apiRequest("POST", `/api/app/admin/beta-features/${featureKey}/users`, { userId });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/admin/beta-features", selectedKey, "users"] });
      queryClient.invalidateQueries({ queryKey: ["/api/app/admin/beta-features"] });
      setSearchQuery("");
      toast({ title: "Access granted" });
    },
    onError: (err: any) => toast({ title: "Error", description: parseErrorMessage(err, "Unknown error"), variant: "destructive" }),
  });

  const revokeMutation = useMutation({
    mutationFn: async ({ featureKey, userId }: { featureKey: string; userId: number }) => {
      await apiRequest("DELETE", `/api/app/admin/beta-features/${featureKey}/users/${userId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/admin/beta-features", selectedKey, "users"] });
      queryClient.invalidateQueries({ queryKey: ["/api/app/admin/beta-features"] });
      toast({ title: "Access revoked" });
    },
    onError: (err: any) => toast({ title: "Error", description: parseErrorMessage(err, "Unknown error"), variant: "destructive" }),
  });

  return (
      <AdminAccessGuard error={featuresQuery.error}>
      <div className="p-6 space-y-4 max-w-6xl" data-tour="admin-beta-features-section">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-2xl font-semibold" data-testid="heading-app-admin-beta">Beta Access</h1>
            <p className="text-sm text-muted-foreground">Member Cleaning, Owner Portal and Quo Messaging are generally available. AI Drafting access is managed by plan in Menu Tier Locks, not beta grants.</p>
            <p className="text-sm text-muted-foreground">
              Grant or revoke access to beta features for users on your platform.
            </p>
          </div>
          <GuideButton pageKey="admin-beta-features" compactOnMobile />
        </div>
        <GuideNudgeBanner pageKey="admin-beta-features" />
        <BetaToolManagers canAssign={canAssign} />

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* Feature list */}
          <Card className="lg:col-span-1">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium">Beta Features</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              {featuresQuery.isLoading ? (
                <div className="p-4 space-y-2">
                  {[1, 2, 3].map((i) => <Skeleton key={i} className="h-12 w-full" />)}
                </div>
              ) : features.length === 0 ? (
                <div className="p-6 text-center text-sm text-muted-foreground">
                  No beta features defined yet. Ask a super-admin to create one.
                </div>
              ) : (
                <div className="divide-y">
                  {features.map((f) => (
                    <button
                      key={f.key}
                      onClick={() => setSelectedKey(f.key === selectedKey ? null : f.key)}
                      className={`w-full text-left px-4 py-3 hover:bg-accent transition-colors ${selectedKey === f.key ? "bg-accent" : ""}`}
                      data-testid={`beta-feature-row-${f.key}`}
                    >
                      <div className="flex items-center justify-between gap-2">
                        <div className="min-w-0">
                          <div className="text-sm font-medium truncate">{f.name}</div>
                          <div className="text-xs text-muted-foreground font-mono">{f.key}</div>
                        </div>
                        <div className="flex items-center gap-1.5 shrink-0">
                          <Badge variant={f.isActive ? "default" : "outline"} className="text-[10px] px-1.5 py-0">
                            {f.isActive ? "Active" : "Off"}
                          </Badge>
                          <span className="text-xs text-muted-foreground" data-testid={`text-beta-user-count-${f.key}`}>
                            {f.userCount}
                          </span>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Detail panel */}
          <Card className="lg:col-span-2">
            {!selectedFeature ? (
              <CardContent className="flex items-center justify-center h-40 text-sm text-muted-foreground">
                Select a feature to manage user access.
              </CardContent>
            ) : (
              <>
                <CardHeader className="pb-3">
                  <CardTitle className="text-base">{selectedFeature.name}</CardTitle>
                  <CardDescription className="font-mono text-xs mt-0.5">{selectedFeature.key}</CardDescription>
                  {selectedFeature.description && (
                    <p className="text-sm text-muted-foreground mt-1.5">{selectedFeature.description}</p>
                  )}
                  {!selectedFeature.isActive && (
                    <p className="text-xs text-amber-600 mt-2">
                      This feature is currently disabled globally. Granting access will have no effect until a super-admin enables it.
                    </p>
                  )}
                </CardHeader>

                <CardContent className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h4 className="text-sm font-medium">Users with access ({usersQuery.data?.length ?? 0})</h4>
                  </div>

                  {/* Grant access search */}
                  <div className="relative">
                    <Input
                      placeholder="Search users by email or name to grant access..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="text-sm"
                      data-testid="input-beta-user-search"
                    />
                    {searchResultsQuery.data && searchResultsQuery.data.length > 0 && (
                      <div className="absolute top-full left-0 right-0 z-10 mt-1 rounded-md border bg-background shadow-md max-h-64 overflow-y-auto">
                        {searchResultsQuery.data.map((u) => (
                          <button
                            key={u.id}
                            className="w-full flex items-center justify-between gap-2 px-3 py-2 text-sm hover:bg-accent"
                            onClick={() => grantMutation.mutate({ featureKey: selectedFeature.key, userId: u.id })}
                            disabled={grantMutation.isPending}
                            data-testid={`button-grant-beta-user-${u.id}`}
                          >
                            <div className="min-w-0 text-left">
                              <div className="font-medium truncate">{u.email}</div>
                              {u.name && <div className="text-xs text-muted-foreground truncate">{u.name}</div>}
                            </div>
                            <UserPlus2 className="h-4 w-4 text-blue-600 shrink-0" />
                          </button>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Users list */}
                  {usersQuery.isLoading ? (
                    <div className="space-y-2">
                      {[1, 2].map((i) => <Skeleton key={i} className="h-10 w-full" />)}
                    </div>
                  ) : (usersQuery.data?.length ?? 0) === 0 ? (
                    <p className="text-sm text-muted-foreground text-center py-4">No users have access yet.</p>
                  ) : (
                    <div className="divide-y rounded-md border overflow-hidden">
                      {usersQuery.data?.map((u) => (
                        <div
                          key={u.id}
                          className="flex items-center justify-between gap-2 px-3 py-2.5"
                          data-testid={`beta-user-row-${u.id}`}
                        >
                          <div className="min-w-0">
                            <div className="text-sm font-medium truncate">{u.email}</div>
                            {u.name && <div className="text-xs text-muted-foreground truncate">{u.name}</div>}
                            <div className="text-xs text-muted-foreground">
                              Granted {new Date(u.grantedAt).toLocaleDateString()}
                            </div>
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-7 px-2 text-red-600 hover:text-red-700 hover:bg-red-50"
                            onClick={() => revokeMutation.mutate({ featureKey: selectedFeature.key, userId: u.id })}
                            disabled={revokeMutation.isPending}
                            data-testid={`button-revoke-beta-user-${u.id}`}
                          >
                            <UserMinus className="h-4 w-4" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </>
            )}
          </Card>
        </div>

        <FeatureGatesManager />
      </div>
      </AdminAccessGuard>
  );
}

// ─── Task #3976 — Feature Gates (Coming Soon / Hidden) ────────────────────────
// Distinct from the beta-access list above: these are the in-code "gateable
// targets" (whole sections + Revenue sub-tabs). Per target an admin can turn the
// gate on, pick Hidden vs Coming Soon, edit the shown message, and grant/revoke
// per-user bypass. After any edit we invalidate BOTH the admin list and the
// user-facing resolver so the change reflects on the next load/refocus.
function FeatureGatesManager() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const gatesQuery = useQuery<GateTarget[]>({
    queryKey: ["/api/app/admin/feature-gates"],
  });
  const gates = (gatesQuery.data ?? []).filter(gate => isAdminGateKey(gate.key));

  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const selected = gates.find((g) => g.key === selectedKey) ?? null;

  const [messageDraft, setMessageDraft] = useState("");
  useEffect(() => {
    const g = gates.find((x) => x.key === selectedKey);
    setMessageDraft(g?.gateMessage ?? "");
    // Only reset when switching targets — avoids clobbering in-progress edits.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedKey]);

  const usersQuery = useQuery<BetaUser[]>({
    queryKey: ["/api/app/admin/feature-gates", selectedKey, "users"],
    queryFn: async () => {
      if (!selectedKey) return [];
      const res = await fetch(`/api/app/admin/feature-gates/${selectedKey}/users`);
      if (!res.ok) throw new Error("Failed to load");
      return res.json();
    },
    enabled: !!selectedKey,
  });

  const [searchQuery, setSearchQuery] = useState("");
  const searchResultsQuery = useQuery<SearchUser[]>({
    queryKey: ["/api/app/admin/beta-search-users", searchQuery],
    queryFn: async () => {
      if (!searchQuery || searchQuery.length < 2) return [];
      const res = await fetch(`/api/app/admin/beta-search-users?q=${encodeURIComponent(searchQuery)}`);
      if (!res.ok) return [];
      return res.json();
    },
    enabled: searchQuery.length >= 2,
  });

  function invalidateGateState() {
    queryClient.invalidateQueries({ queryKey: ["/api/app/admin/feature-gates"] });
    // The user-facing resolver + the beta-access list both reflect gate rows.
    queryClient.invalidateQueries({ queryKey: ["/api/app/feature-gates"] });
    queryClient.invalidateQueries({ queryKey: ["/api/app/admin/beta-features"] });
  }

  const patchMutation = useMutation({
    mutationFn: async ({
      key,
      ...body
    }: {
      key: string;
      gateEnabled?: boolean;
      gateMode?: "hidden" | "coming_soon";
      gateMessage?: string | null;
    }) => {
      await apiRequest("PATCH", `/api/app/admin/feature-gates/${key}`, body);
    },
    onSuccess: () => invalidateGateState(),
    onError: (err) =>
      toast({ title: "Error", description: parseErrorMessage(err, "Failed to update gate"), variant: "destructive" }),
  });

  const grantMutation = useMutation({
    mutationFn: async ({ key, userId }: { key: string; userId: number }) => {
      await apiRequest("POST", `/api/app/admin/feature-gates/${key}/users`, { userId });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/admin/feature-gates", selectedKey, "users"] });
      invalidateGateState();
      setSearchQuery("");
      toast({ title: "Bypass granted" });
    },
    onError: (err) =>
      toast({ title: "Error", description: parseErrorMessage(err, "Failed to grant bypass"), variant: "destructive" }),
  });

  const revokeMutation = useMutation({
    mutationFn: async ({ key, userId }: { key: string; userId: number }) => {
      await apiRequest("DELETE", `/api/app/admin/feature-gates/${key}/users/${userId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/admin/feature-gates", selectedKey, "users"] });
      invalidateGateState();
      toast({ title: "Bypass revoked" });
    },
    onError: (err) =>
      toast({ title: "Error", description: parseErrorMessage(err, "Failed to revoke bypass"), variant: "destructive" }),
  });

  const messageDirty = selected != null && messageDraft !== (selected.gateMessage ?? "");

  return (
    <Card data-tour="admin-feature-gates-section">
      <CardHeader className="pb-3">
        <CardTitle className="text-base">Feature Gates (Coming Soon / Hidden)</CardTitle>
        <CardDescription>
          Take a live feature and temporarily hide it, or keep its nav visible but show a “Coming Soon” message.
          Admins and the people you grant below always bypass the gate.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* Target list */}
          <div className="lg:col-span-1 rounded-md border overflow-hidden">
            {gatesQuery.isLoading ? (
              <div className="p-4 space-y-2">
                {[1, 2, 3].map((i) => <Skeleton key={i} className="h-10 w-full" />)}
              </div>
            ) : gates.length === 0 ? (
              <div className="p-6 text-center text-sm text-muted-foreground">No gateable features.</div>
            ) : (
              <div className="divide-y">
                {gates.map((g) => (
                  <button
                    key={g.key}
                    onClick={() => setSelectedKey(g.key === selectedKey ? null : g.key)}
                    className={`w-full text-left px-4 py-2.5 hover:bg-accent transition-colors ${selectedKey === g.key ? "bg-accent" : ""} ${g.group ? "pl-8" : ""}`}
                    data-testid={`gate-target-row-${g.key}`}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <div className="min-w-0">
                        <div className="text-sm font-medium truncate">{g.name}</div>
                        <div className="text-xs text-muted-foreground font-mono truncate">{g.key}</div>
                      </div>
                      {g.gateEnabled && (
                        <Badge
                          variant="outline"
                          className="text-[10px] px-1.5 py-0 shrink-0 gap-1"
                          data-testid={`gate-badge-${g.key}`}
                        >
                          {g.gateMode === "coming_soon" ? (
                            <><Megaphone className="h-3 w-3" /> Soon</>
                          ) : (
                            <><EyeOff className="h-3 w-3" /> Hidden</>
                          )}
                        </Badge>
                      )}
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Detail panel */}
          <div className="lg:col-span-2">
            {!selected ? (
              <div className="flex items-center justify-center h-40 text-sm text-muted-foreground border rounded-md">
                Select a feature to manage its gate.
              </div>
            ) : (
              <div className="space-y-5">
                <div>
                  <h3 className="text-sm font-semibold">{selected.name}</h3>
                  <p className="text-xs text-muted-foreground font-mono">{selected.key}</p>
                  {selected.description && (
                    <p className="text-sm text-muted-foreground mt-1">{selected.description}</p>
                  )}
                </div>

                {/* Controlled surfaces */}
                <div className="rounded-md bg-muted/50 p-3 text-xs space-y-1">
                  <div className="font-medium text-muted-foreground">This gate controls:</div>
                  {selected.navHrefs.length > 0 && (
                    <div data-testid={`gate-nav-${selected.key}`}>
                      <span className="text-muted-foreground">Nav: </span>
                      <span className="font-mono">{selected.navHrefs.join(", ")}</span>
                    </div>
                  )}
                  <div data-testid={`gate-routes-${selected.key}`}>
                    <span className="text-muted-foreground">Routes: </span>
                    <span className="font-mono">{[
                      ...selected.routePrefixes,
                      ...(selected.routeExactPaths ?? []).map((path) => `${path} (exact)`),
                    ].join(", ") || "—"}</span>
                  </div>
                  <div data-testid={`gate-apis-${selected.key}`}>
                    <span className="text-muted-foreground">API: </span>
                    <span className="font-mono">{selected.apiPrefixes.join(", ") || "—"}</span>
                  </div>
                </div>

                {/* Gate on/off */}
                <div className="flex items-center justify-between gap-4">
                  <div>
                    <Label htmlFor={`gate-enabled-${selected.key}`} className="text-sm">Gate enabled</Label>
                    <p className="text-xs text-muted-foreground">When off, the feature behaves normally for everyone.</p>
                  </div>
                  <Switch
                    id={`gate-enabled-${selected.key}`}
                    checked={selected.gateEnabled}
                    disabled={patchMutation.isPending}
                    onCheckedChange={(checked) => patchMutation.mutate({ key: selected.key, gateEnabled: checked })}
                    data-testid={`switch-gate-enabled-${selected.key}`}
                  />
                </div>

                {/* Mode */}
                <div className="space-y-2">
                  <Label className="text-sm">Display mode</Label>
                  <div className="flex gap-2">
                    <Button
                      type="button"
                      size="sm"
                      variant={selected.gateMode === "hidden" ? "default" : "outline"}
                      disabled={patchMutation.isPending}
                      onClick={() => patchMutation.mutate({ key: selected.key, gateMode: "hidden" })}
                      data-testid={`button-gate-mode-hidden-${selected.key}`}
                    >
                      <EyeOff className="h-4 w-4 mr-1.5" /> Hidden
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant={selected.gateMode === "coming_soon" ? "default" : "outline"}
                      disabled={patchMutation.isPending}
                      onClick={() => patchMutation.mutate({ key: selected.key, gateMode: "coming_soon" })}
                      data-testid={`button-gate-mode-coming-soon-${selected.key}`}
                    >
                      <Megaphone className="h-4 w-4 mr-1.5" /> Coming Soon
                    </Button>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {selected.gateMode === "coming_soon"
                      ? "Nav item stays visible; clicking shows the message below. Data/API stays blocked."
                      : "Nav item is hidden and the route is blocked for non-exempt users."}
                  </p>
                </div>

                {/* Coming Soon message */}
                <div className="space-y-2">
                  <Label htmlFor={`gate-message-${selected.key}`} className="text-sm">Coming Soon message</Label>
                  <Textarea
                    id={`gate-message-${selected.key}`}
                    value={messageDraft}
                    onChange={(e) => setMessageDraft(e.target.value)}
                    placeholder={GATE_MESSAGE_PLACEHOLDER}
                    rows={3}
                    maxLength={2000}
                    className="text-sm"
                    data-testid={`textarea-gate-message-${selected.key}`}
                  />
                  <div className="flex justify-end">
                    <Button
                      type="button"
                      size="sm"
                      disabled={!messageDirty || patchMutation.isPending}
                      onClick={() => patchMutation.mutate({ key: selected.key, gateMessage: messageDraft.trim() === "" ? null : messageDraft })}
                      data-testid={`button-save-gate-message-${selected.key}`}
                    >
                      Save message
                    </Button>
                  </div>
                </div>

                {/* Per-user bypass */}
                <div className="space-y-2 border-t pt-4">
                  <Label className="text-sm">Users who bypass this gate ({usersQuery.data?.length ?? 0})</Label>
                  <div className="relative">
                    <Input
                      placeholder="Search users by email or name to grant bypass..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="text-sm"
                      data-testid={`input-gate-user-search-${selected.key}`}
                    />
                    {searchResultsQuery.data && searchResultsQuery.data.length > 0 && (
                      <div className="absolute top-full left-0 right-0 z-10 mt-1 rounded-md border bg-background shadow-md max-h-64 overflow-y-auto">
                        {searchResultsQuery.data.map((u) => (
                          <button
                            key={u.id}
                            className="w-full flex items-center justify-between gap-2 px-3 py-2 text-sm hover:bg-accent"
                            onClick={() => grantMutation.mutate({ key: selected.key, userId: u.id })}
                            disabled={grantMutation.isPending}
                            data-testid={`button-grant-gate-user-${u.id}`}
                          >
                            <div className="min-w-0 text-left">
                              <div className="font-medium truncate">{u.email}</div>
                              {u.name && <div className="text-xs text-muted-foreground truncate">{u.name}</div>}
                            </div>
                            <UserPlus2 className="h-4 w-4 text-brand shrink-0" />
                          </button>
                        ))}
                      </div>
                    )}
                  </div>

                  {usersQuery.isLoading ? (
                    <div className="space-y-2">
                      {[1, 2].map((i) => <Skeleton key={i} className="h-9 w-full" />)}
                    </div>
                  ) : (usersQuery.data?.length ?? 0) === 0 ? (
                    <p className="text-sm text-muted-foreground text-center py-3">No users bypass this gate yet.</p>
                  ) : (
                    <div className="divide-y rounded-md border overflow-hidden">
                      {usersQuery.data?.map((u) => (
                        <div
                          key={u.id}
                          className="flex items-center justify-between gap-2 px-3 py-2"
                          data-testid={`gate-user-row-${u.id}`}
                        >
                          <div className="min-w-0">
                            <div className="text-sm font-medium truncate">{u.email}</div>
                            {u.name && <div className="text-xs text-muted-foreground truncate">{u.name}</div>}
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-7 px-2 text-destructive hover:text-destructive"
                            onClick={() => revokeMutation.mutate({ key: selected.key, userId: u.id })}
                            disabled={revokeMutation.isPending}
                            data-testid={`button-revoke-gate-user-${u.id}`}
                          >
                            <UserMinus className="h-4 w-4" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
